import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../services/axios';

// Create App Release
export const createAppRelease = createAsyncThunk(
  'appRelease/create',
  async (releaseData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post('/app-releases', releaseData);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// Get App Releases
export const fetchAppReleases = createAsyncThunk(
  'appRelease/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get('/app-releases/all');
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// Update App Release Status
export const updateAppReleaseStatus = createAsyncThunk(
  'appRelease/updateStatus',
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.patch(`/app-releases/${id}/status`, { status });
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// Delete App Release
export const deleteAppRelease = createAsyncThunk(
  'appRelease/delete',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.delete(`/app-releases/${id}`);
      return { id, ...response.data };
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

const appReleaseSlice = createSlice({
  name: 'appRelease',
  initialState: {
    releases: [],
    loading: false,
    error: null,
    createLoading: false,
    updateLoading: false,
    deleteLoading: false,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create App Release
      .addCase(createAppRelease.pending, (state) => {
        state.createLoading = true;
        state.error = null;
      })
      .addCase(createAppRelease.fulfilled, (state, action) => {
        state.createLoading = false;
        const newRelease = action.payload.data || action.payload;
        // Ensure releases is an array before using unshift
        if (Array.isArray(state.releases)) {
          state.releases.unshift(newRelease);
        } else {
          state.releases = [newRelease];
        }
      })
      .addCase(createAppRelease.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload || 'Failed to create app release';
      })

      // Fetch App Releases
      .addCase(fetchAppReleases.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAppReleases.fulfilled, (state, action) => {
        state.loading = false;
        // Ensure we always get an array
        const responseData = action.payload.data || action.payload;
        state.releases = Array.isArray(responseData) ? responseData : [];
      })
      .addCase(fetchAppReleases.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch app releases';
      })

      // Update App Release Status
      .addCase(updateAppReleaseStatus.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
      })
      .addCase(updateAppReleaseStatus.fulfilled, (state, action) => {
        state.updateLoading = false;
        const updatedRelease = action.payload.data || action.payload;
        // Ensure releases is an array before using findIndex
        if (Array.isArray(state.releases)) {
          const index = state.releases.findIndex(release => release.id === updatedRelease.id);
          if (index !== -1) {
            state.releases[index] = { ...state.releases[index], ...updatedRelease };
          }
        }
      })
      .addCase(updateAppReleaseStatus.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload || 'Failed to update app release status';
      })

      // Delete App Release
      .addCase(deleteAppRelease.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
      })
      .addCase(deleteAppRelease.fulfilled, (state, action) => {
        state.deleteLoading = false;
        // Ensure releases is an array before using filter
        if (Array.isArray(state.releases)) {
          state.releases = state.releases.filter(release => release.id !== action.payload.id);
        }
      })
      .addCase(deleteAppRelease.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload || 'Failed to delete app release';
      });
  },
});

export const { clearError } = appReleaseSlice.actions;
export default appReleaseSlice.reducer;
